/**
 * Admin - Nova empresa: assistente em 7 etapas, todo em estado local.
 *
 * Decisoes nao obvias:
 *  - As etapas sao MONTADAS uma por vez (o formulario inteiro vive na
 *    pagina), e ao entrar em certas etapas a pagina PRE-PREENCHE o que da
 *    para deduzir: provedores a partir das ofertas (etapa 5), instancia e
 *    assistente a partir do nome, dono a partir do contato (etapa 7). A
 *    pessoa so corrige, em vez de digitar de novo.
 *  - Erros so aparecem depois da primeira tentativa de avancar naquela etapa
 *    (`tentou[etapa]`). "Criar" valida TODAS e pula para a primeira invalida.
 *  - Enter dentro de um campo avanca a etapa (o `<form>` envolve a etapa);
 *    a lista de ofertas intercepta o Enter para adicionar, nao avancar.
 *  - Ao trocar de etapa o foco vai para o titulo (sr-only) da nova etapa, para
 *    o leitor de tela anunciar onde a pessoa esta.
 *  - Nada e enviado: o "Criar empresa (previa)" monta o resumo com a data
 *    fixa da previa (`AGORA_SIMULADO`) e as URLs de webhook derivadas do id.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SimulatedNotice } from '../../../components/PageState';
import { Badge, Button, HeroBanner, LinkButton, Stepper } from '../../../components/ui';
import { AGORA_SIMULADO } from '../../../lib/api';
import {
  ETAPAS,
  FORM_INICIAL,
  ULTIMA_ETAPA,
  etapaValida,
  montarEmpresaCriada,
  primeiraEtapaInvalida,
  provedoresDasOfertas,
  sugerirAssistente,
  sugerirInstancia,
  validarEtapa,
  type EmpresaCriada,
  type FormNovaEmpresa,
} from '../assistente';
import { EtapaAcesso } from '../components/assistente/EtapaAcesso';
import { EtapaComissao } from '../components/assistente/EtapaComissao';
import { EtapaDados } from '../components/assistente/EtapaDados';
import { EtapaFunis } from '../components/assistente/EtapaFunis';
import { EtapaIntegracoes } from '../components/assistente/EtapaIntegracoes';
import { EtapaMarca } from '../components/assistente/EtapaMarca';
import { EtapaOfertas } from '../components/assistente/EtapaOfertas';
import { TelaSucesso } from '../components/assistente/TelaSucesso';

/** O que a pagina deduz ao ENTRAR numa etapa, sem sobrescrever o que ja foi digitado. */
function preencherAoEntrar(etapa: number, form: FormNovaEmpresa): FormNovaEmpresa {
  let proximo = form;
  if (etapa === 4) {
    if (proximo.provedores.length === 0) {
      proximo = { ...proximo, provedores: provedoresDasOfertas(proximo) };
    }
    if (proximo.evolutionInstancia === '') {
      proximo = { ...proximo, evolutionInstancia: sugerirInstancia(proximo.nome) };
    }
    if (proximo.vapiAssistente === '') {
      proximo = { ...proximo, vapiAssistente: sugerirAssistente(proximo.nome) };
    }
  }
  if (etapa === 6 && proximo.donoNome === '' && proximo.donoEmail === '') {
    proximo = { ...proximo, donoNome: proximo.contatoNome, donoEmail: proximo.contatoEmail };
  }
  return proximo;
}

export function AdminEmpresaNova(): JSX.Element {
  const [etapa, setEtapa] = useState(0);
  const [form, setForm] = useState<FormNovaEmpresa>(FORM_INICIAL);
  const [tentou, setTentou] = useState<readonly boolean[]>(() => ETAPAS.map(() => false));
  const [criada, setCriada] = useState<EmpresaCriada | null>(null);
  const tituloEtapaRef = useRef<HTMLHeadingElement | null>(null);

  useEffect(() => {
    document.title = 'Nova empresa — Administração · Converte Fácil';
  }, []);

  useEffect(() => {
    tituloEtapaRef.current?.focus();
  }, [etapa]);

  const erros = useMemo(() => validarEtapa(etapa, form), [etapa, form]);
  const mostrarErros = tentou[etapa] ?? false;
  const temErros = Object.keys(erros).length > 0;

  const onChange = useCallback((patch: Partial<FormNovaEmpresa>): void => {
    setForm((anterior) => ({ ...anterior, ...patch }));
  }, []);

  const irPara = (proxima: number): void => {
    const alvo = Math.max(0, Math.min(ULTIMA_ETAPA, proxima));
    setForm((anterior) => preencherAoEntrar(alvo, anterior));
    setEtapa(alvo);
  };

  const continuar = (): void => {
    setTentou((lista) => lista.map((v, i) => (i === etapa ? true : v)));
    if (etapaValida(etapa, form)) {
      irPara(etapa + 1);
    }
  };

  const criar = (): void => {
    setTentou(ETAPAS.map(() => true));
    const invalida = primeiraEtapaInvalida(form);
    if (invalida !== null) {
      irPara(invalida);
      return;
    }
    setCriada(montarEmpresaCriada(form, AGORA_SIMULADO.toISOString()));
  };

  const reiniciar = (): void => {
    setForm(FORM_INICIAL);
    setTentou(ETAPAS.map(() => false));
    setEtapa(0);
    setCriada(null);
  };

  const propsEtapa = { form, onChange, erros, mostrarErros };
  const rotuloEtapa = ETAPAS[etapa]?.label ?? '';

  return (
    <section aria-labelledby="titulo-da-pagina" className="flex flex-col gap-6">
      <HeroBanner
        icon="empresa"
        title="Nova empresa"
        backTo={
          <LinkButton to="/admin/empresas" variant="ghost" icon="seta-esq">
            <span className="sr-only">Voltar para a lista de empresas</span>
          </LinkButton>
        }
        badges={
          criada === null ? (
            <Badge tone="brand">
              Etapa {etapa + 1} de {ETAPAS.length}
            </Badge>
          ) : (
            <Badge tone="good" icon="check">
              Criada na prévia
            </Badge>
          )
        }
        description="Sete etapas: dados, marca, comissão e cobrança (com a política de estorno), ofertas, integrações (Kiwify, Hotmart, Shopify ou Genérico), funis e acesso. No fim, as URLs de webhook saem prontas para colar e a comissão continua editável na aba da empresa."
      />

      <SimulatedNotice>Este assistente funciona em estado local: nada é enviado ao servidor nem gravado em banco. Recarregar a página apaga o que foi digitado.</SimulatedNotice>

      {criada !== null ? (
        <TelaSucesso criada={criada} onNova={reiniciar} />
      ) : (
        <>
          <Stepper steps={ETAPAS} current={etapa} onGoTo={irPara} />

          <form
            noValidate
            className="flex flex-col gap-6"
            onSubmit={(event) => {
              event.preventDefault();
              if (etapa === ULTIMA_ETAPA) {
                criar();
              } else {
                continuar();
              }
            }}
          >
            <h2 ref={tituloEtapaRef} tabIndex={-1} className="sr-only">
              Etapa {etapa + 1} de {ETAPAS.length}: {rotuloEtapa}
            </h2>

            {etapa === 0 ? <EtapaDados {...propsEtapa} /> : null}
            {etapa === 1 ? <EtapaMarca {...propsEtapa} /> : null}
            {etapa === 2 ? <EtapaComissao {...propsEtapa} /> : null}
            {etapa === 3 ? <EtapaOfertas {...propsEtapa} /> : null}
            {etapa === 4 ? <EtapaIntegracoes {...propsEtapa} /> : null}
            {etapa === 5 ? <EtapaFunis {...propsEtapa} /> : null}
            {etapa === 6 ? <EtapaAcesso {...propsEtapa} onGoTo={irPara} /> : null}

            <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-line bg-surface p-4 shadow-soft">
              <div className="flex flex-wrap items-center gap-2">
                <Button variant="secondary" icon="seta-esq" onClick={() => irPara(etapa - 1)} disabled={etapa === 0}>
                  Voltar
                </Button>
                <LinkButton to="/admin/empresas" variant="ghost">
                  Cancelar
                </LinkButton>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <span role="status">
                  {mostrarErros && temErros ? <span className="text-xs text-status-critical">Corrija os campos destacados para continuar.</span> : null}
                </span>
                {etapa === ULTIMA_ETAPA ? (
                  <Button type="submit" variant="primary" icon="check" size="lg">
                    Criar empresa (prévia)
                  </Button>
                ) : (
                  <Button type="submit" variant="primary" iconRight="seta-dir" size="lg">
                    Continuar
                  </Button>
                )}
              </div>
            </div>
          </form>
        </>
      )}
    </section>
  );
}
